import React, { useState, useEffect, useCallback, Suspense } from "react";
import { Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { 
    Shield, 
    Menu,
    X,
    ArrowRight,
    Award,
    Heart,
    Database,
    Home as HomeIcon,
    Users,
    ChevronRight,
    Globe,
    Cpu,
    Wrench,
    Clock,
    Activity,
    Radio,
    Film
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lazy load pages
const GuardianVault = React.lazy(() => import("@/pages/GuardianVault"));
const AboutUs = React.lazy(() => import("@/pages/AboutUs"));

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6e02ee9e-f7c9-4490-bd62-9bfd4af71f80/artifacts/qo1ysu8n_sentinel_web.jpg";
const NVIDIA_URL = "https://customer-assets.emergentagent.com/job_6e02ee9e-f7c9-4490-bd62-9bfd4af71f80/artifacts/1cfq610o_image.png";
const FOUNDER_URL = "https://customer-assets.emergentagent.com/job_71c9b8c9-e8dc-4ff4-a0ce-772098d07065/artifacts/l3l8gm3y_IMG_5156.jpeg";

// Splash Screen Component - Static centered logo
const SplashScreen = ({ onEmerge, onComplete }) => {
    const [fading, setFading] = useState(false);

    useEffect(() => {
        const timers = [
            setTimeout(() => {
                setFading(true);
                onEmerge();
            }, 2200),
            setTimeout(() => onComplete(), 3200),
        ];
        return () => timers.forEach(clearTimeout);
    }, [onEmerge, onComplete]);

    return (
        <div 
            className={`splash-screen ${fading ? 'splash-fading' : ''}`}
            data-testid="splash-screen"
        >
            <div className="flex items-center gap-8">
                <img src={LOGO_URL} alt="Sentinel Systems Group" className="splash-logo-static" />
                <img src={NVIDIA_URL} alt="NVIDIA Inception Program" className="splash-logo-static" />
            </div>
        </div>
    );
};

// Navigation Component
const Navigation = ({ mobileMenuOpen, setMobileMenuOpen }) => {
    const navLinks = [
        { href: "#mission", label: "Mission" },
        { href: "#ecosystem", label: "Ecosystem" },
        { href: "#founder", label: "Founder" },
        { href: "#foundation", label: "Foundation" },
        { href: "/about", label: "About Us", isRoute: true },
        { href: "#contact", label: "Contact" },
    ];

    return (
        <>
            <nav className="fixed top-0 left-0 right-0 z-40 glass" data-testid="main-navigation">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <a href="#" className="flex items-center gap-3" data-testid="logo-link">
                            <img src={LOGO_URL} alt="Sentinel" className="h-12 w-auto" />
                            <img src={NVIDIA_URL} alt="NVIDIA" className="h-10 w-auto" />
                        </a>

                        <div className="hidden md:flex items-center gap-8">
                            {navLinks.map((link) => (
                                link.isRoute ? (
                                    <Link
                                        key={link.href}
                                        to={link.href}
                                        className="nav-link text-sm"
                                        data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                                    >
                                        {link.label}
                                    </Link>
                                ) : (
                                    <a 
                                        key={link.href}
                                        href={link.href} 
                                        className="nav-link text-sm"
                                        data-testid={`nav-${link.label.toLowerCase()}`}
                                    >
                                        {link.label}
                                    </a>
                                )
                            ))}
                            <a href="#contact">
                                <Button 
                                    className="btn-primary px-6 py-2"
                                    data-testid="nav-get-started-btn"
                                >
                                    Connect
                                </Button>
                            </a>
                        </div>

                        <button 
                            className="md:hidden p-2 text-gray-400 hover:text-white"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </nav>

            {mobileMenuOpen && (
                <div className="mobile-menu" data-testid="mobile-menu">
                    {navLinks.map((link) => (
                        link.isRoute ? (
                            <Link
                                key={link.href}
                                to={link.href}
                                className="mobile-nav-link"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {link.label}
                            </Link>
                        ) : (
                            <a 
                                key={link.href}
                                href={link.href} 
                                className="mobile-nav-link"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {link.label}
                            </a>
                        )
                    ))}
                    <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="btn-primary px-8 py-3">Connect</Button>
                    </a>
                </div>
            )}
        </>
    );
};

// Hero Section
const HeroSection = () => {
    return (
        <section className="hero-section tech-grid" data-testid="hero-section">
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
                <div className="max-w-4xl">
                    {/* SDVOSB Badge */}
                    <div className="sdvosb-badge mb-8 animate-fade-in-up" data-testid="sdvosb-badge">
                        <Award className="w-4 h-4" />
                        <span>Service-Disabled Veteran-Owned Small Business</span>
                    </div>
                    
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6 animate-fade-in-up-delay-1">
                        Restoring Autonomy Through<br />
                        <span className="text-cyan-400">Digital Cognitive Prosthetics</span>
                    </h1>
                    
                    <p className="text-lg sm:text-xl text-gray-400 mb-10 max-w-2xl animate-fade-in-up-delay-2 leading-relaxed">
                        We build intelligent systems that extend human capability—bridging the gap 
                        between limitation and potential through technology designed for those who've given everything.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up-delay-3">
                        <a href="#ecosystem">
                            <Button className="btn-primary text-base" data-testid="hero-explore-btn">
                                Explore the Ecosystem
                                <ArrowRight className="w-5 h-5" />
                            </Button>
                        </a>
                        <a href="#founder">
                            <Button variant="ghost" className="btn-secondary text-base" data-testid="hero-story-btn">
                                Our Story
                                <ChevronRight className="w-5 h-5" />
                            </Button>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Mission Section
const MissionSection = () => {
    return (
        <section id="mission" className="py-24 md:py-32 bg-[#050709]" data-testid="mission-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto text-center">
                    <span className="section-tag">Our Mission</span>
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
                        Technology as a <span className="text-cyan-400">Force Multiplier</span>
                    </h2>
                    <p className="text-lg text-gray-400 leading-relaxed mb-8">
                        At Sentinel Systems Group, we believe that cognitive challenges shouldn't define capability. 
                        Our digital cognitive prosthetics are designed to augment human potential—providing the 
                        tools, systems, and support structures that enable independence and autonomy.
                    </p>
                    <p className="text-lg text-gray-400 leading-relaxed">
                        Born from lived experience and forged through adversity, we create solutions 
                        that understand the unique needs of those navigating invisible wounds.
                    </p>
                </div>
            </div>
        </section>
    );
};

// Ecosystem Section
const EcosystemSection = () => {
    const products = [
        {
            icon: Database,
            name: "Sentinel Vault",
            tagline: "Your Life, Secured",
            description: "A comprehensive records management system that organizes, protects, and provides instant access to critical documents—medical records, legal papers, benefits information—when you need them most."
        },
        {
            icon: HomeIcon,
            name: "Sentinel Home",
            tagline: "Guardian OS",
            description: "An intelligent home operating system that creates adaptive environments. Ambient reminders, routine support, and seamless integration with your daily life—technology that works for you, not against you."
        },
        {
            icon: Wrench,
            name: "The Handyman",
            tagline: "AI Home Maintenance",
            description: "AI-driven home maintenance and repair assistance. Diagnose issues, schedule repairs, and get step-by-step guidance—keeping your environment safe and functional without the cognitive overhead."
        },
        {
            icon: Clock,
            name: "The Chronos",
            tagline: "Cognitive Scheduling",
            description: "Executive task management and cognitive scheduling designed for minds navigating TBI fog. Prioritize, sequence, and execute daily tasks with AI that understands your capacity."
        },
        {
            icon: Activity,
            name: "The Warden",
            tagline: "Sensory Calibration",
            description: "Sensory calibration and environmental tuning. Monitors and adjusts your surroundings—lighting, sound, temperature—to optimize cognitive function and reduce sensory overload."
        },
        {
            icon: Radio,
            name: "The Beacon",
            tagline: "Emergency Response",
            description: "Rapid-response emergency communication system. One-touch alerts to caregivers, family, and first responders with real-time location and context—because seconds matter."
        },
        {
            icon: Film,
            name: "Sentinel VFX",
            tagline: "Assistive Visual Toolset",
            description: "AI-powered assistive video and visual toolset. Enhanced visual processing, real-time captioning, and cognitive overlays that help you see, understand, and interact with the world more clearly."
        }
    ];

    return (
        <section id="ecosystem" className="py-24 md:py-32 bg-[#0B0F17]" data-testid="ecosystem-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <span className="section-tag">Product Pipeline</span>
                    <h2 className="text-3xl md:text-5xl font-bold text-white">
                        The Sentinel Ecosystem
                    </h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
                    {products.map((product, index) => (
                        <div 
                            key={index}
                            className="ecosystem-card"
                            data-testid={`ecosystem-card-${index}`}
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
                                    <product.icon className="w-6 h-6 text-cyan-400" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">{product.name}</h3>
                                    <p className="text-xs text-cyan-400 font-mono">{product.tagline}</p>
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                {product.description}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Unified Harmony Section */}
                <div className="border-t border-gray-800 pt-16">
                    <div className="text-center mb-12">
                        <span className="section-tag">Unified Harmony</span>
                        <h3 className="text-2xl md:text-3xl font-bold text-white mt-4">
                            Integrated With Industry Leaders
                        </h3>
                        <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
                            Our ecosystem seamlessly connects with the platforms and devices you already trust.
                        </p>
                    </div>
                    
                    <div className="partner-grid">
                        {['Apple', 'VITURE', 'Starlink', 'Google'].map((partner) => (
                            <span key={partner} className="partner-name">{partner}</span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// Founder Section
const FounderSection = () => {
    const timeline = [
        { text: "Served as Military Policeman in the United States Army" },
        { text: "Sustained injury at 19 while serving in Iraq—awarded the Purple Heart" },
        { text: "Served as Data Analyst for the United States Army Military Police Corps" },
        { text: "Navigated the challenges of a broken system post-2007" },
        { text: "Experienced period of transition—became one of the 'invisible'" },
        { text: "Overcame TBI fog to envision and build Sentinel Systems Group" }
    ];

    return (
        <section id="founder" className="py-24 md:py-32 bg-[#050709]" data-testid="founder-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                    {/* Image */}
                    <div className="order-2 lg:order-1">
                        <div className="founder-image-frame">
                            <img 
                                src={FOUNDER_URL}
                                alt="Founder - Purple Heart Recipient"
                                className="w-full aspect-[4/5] object-cover"
                                loading="lazy"
                                data-testid="founder-image"
                            />
                        </div>
                        <div className="mt-6 flex items-center gap-3">
                            <Heart className="w-5 h-5 text-red-500" />
                            <span className="text-sm text-gray-400 font-mono tracking-wider">PURPLE HEART RECIPIENT</span>
                        </div>
                    </div>
                    
                    {/* Content */}
                    <div className="order-1 lg:order-2">
                        <span className="section-tag">The Founder</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            From Service to <span className="text-cyan-400">Innovation</span>
                        </h2>
                        
                        <p className="text-gray-400 leading-relaxed mb-8">
                            The path to Sentinel wasn't linear—it was forged through service, sacrifice, 
                            and the determination to transform personal struggle into purpose. 
                            What began as a fight for survival became a mission to ensure no veteran 
                            faces their battles alone.
                        </p>
                        
                        {/* Timeline */}
                        <div className="mt-10">
                            {timeline.map((item, index) => (
                                <div key={index} className="timeline-item">
                                    <p className="text-gray-300">{item.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Foundation Section
const FoundationSection = () => {
    return (
        <section id="foundation" className="py-24 md:py-32 bg-[#0B0F17]" data-testid="foundation-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <span className="section-tag">Sentinel Watch Foundation</span>
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                        The <span className="text-white">0mission</span>
                    </h2>
                    <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
                        Every day, 17.5 veterans take their own lives. The Sentinel Watch Foundation exists 
                        to change that number to zero—providing grants, resources, and support systems 
                        for homeless and low-income veterans navigating their darkest hours.
                    </p>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                    <div className="foundation-stat" data-testid="foundation-stat-0">
                        <div className="foundation-stat-number">17.5</div>
                        <div className="foundation-stat-label">Veterans Lost Daily</div>
                    </div>
                    <div className="foundation-stat" data-testid="foundation-stat-1">
                        <div className="foundation-stat-number">0</div>
                        <div className="foundation-stat-label">Our Target</div>
                    </div>
                    <div className="foundation-stat" data-testid="foundation-stat-2">
                        <div className="foundation-stat-number flex items-center justify-center gap-2">
                            <Users className="w-8 h-8" />
                        </div>
                        <div className="foundation-stat-label">Grants for Veterans</div>
                    </div>
                </div>

                {/* Focus Areas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    <div className="p-6 border border-gray-800 bg-black/30">
                        <h3 className="text-lg font-semibold text-white mb-3">Emergency Assistance</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                            Immediate support for veterans facing homelessness, providing emergency housing, 
                            food security, and essential resources during critical transitions.
                        </p>
                    </div>
                    <div className="p-6 border border-gray-800 bg-black/30">
                        <h3 className="text-lg font-semibold text-white mb-3">Technology Grants</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                            Providing low-income veterans access to Sentinel's cognitive support systems 
                            at no cost—ensuring technology serves those who need it most.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Contact Section
const ContactSection = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        service_interest: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleServiceChange = (value) => {
        setFormData(prev => ({ ...prev, service_interest: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.service_interest || !formData.message) {
            toast.error("Please fill in all fields");
            return;
        }

        if (formData.message.length < 10) {
            toast.error("Message must be at least 10 characters");
            return;
        }

        setIsSubmitting(true);

        try {
            await axios.post(`${API}/contact`, formData);
            toast.success("Message received. We'll be in touch.");
            setFormData({ name: "", email: "", service_interest: "", message: "" });
        } catch (error) {
            console.error("Form submission error:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contact" className="py-24 md:py-32 bg-[#050709]" data-testid="contact-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                    <div>
                        <span className="section-tag">Connect</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Begin the <span className="text-cyan-400">Conversation</span>
                        </h2>
                        
                        <p className="text-gray-400 leading-relaxed mb-8">
                            Whether you're a veteran seeking support, a partner exploring collaboration, 
                            or an advocate for our mission—we want to hear from you.
                        </p>
                        
                        <div className="space-y-4 mb-10">
                            <div className="flex items-center gap-3 text-gray-400">
                                <Shield className="w-5 h-5 text-cyan-400" />
                                <span>SDVOSB Certified</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-400">
                                <Globe className="w-5 h-5 text-cyan-400" />
                                <span>sentinalsystemsgroup.tech</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-400">
                                <Cpu className="w-5 h-5 text-cyan-400" />
                                <span>Digital Cognitive Prosthetics</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-[#0B0F17] border border-gray-800 p-8" data-testid="contact-form-container">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                                <Input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Your name"
                                    className="bg-black/50 border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500"
                                    data-testid="contact-name-input"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                                <Input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="your@email.com"
                                    className="bg-black/50 border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500"
                                    data-testid="contact-email-input"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Interest</label>
                                <Select value={formData.service_interest} onValueChange={handleServiceChange}>
                                    <SelectTrigger 
                                        className="bg-black/50 border-gray-700 text-white"
                                        data-testid="contact-service-select"
                                    >
                                        <SelectValue placeholder="Select an option" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ecosystem">Sentinel Ecosystem</SelectItem>
                                        <SelectItem value="foundation">Sentinel Watch Foundation</SelectItem>
                                        <SelectItem value="partnership">Partnership Inquiry</SelectItem>
                                        <SelectItem value="veteran-support">Veteran Support</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                                <Textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Tell us how we can help..."
                                    rows={4}
                                    className="bg-black/50 border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500 resize-none"
                                    data-testid="contact-message-input"
                                />
                            </div>
                            
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full btn-primary py-4"
                                data-testid="contact-submit-btn"
                            >
                                {isSubmitting ? "Sending..." : "Send Message"}
                                {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Footer
const Footer = () => {
    return (
        <footer className="py-12 border-t border-gray-800 bg-black" data-testid="footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <img src={LOGO_URL} alt="Sentinel" className="h-10 w-auto" />
                        <img src={NVIDIA_URL} alt="NVIDIA" className="h-8 w-auto" />
                    </div>
                    
                    <div className="flex items-center gap-8">
                        <a href="#mission" className="footer-link text-sm">Mission</a>
                        <a href="#ecosystem" className="footer-link text-sm">Ecosystem</a>
                        <a href="#founder" className="footer-link text-sm">Founder</a>
                        <a href="#foundation" className="footer-link text-sm">Foundation</a>
                    </div>
                    
                    <p className="text-sm text-gray-600">
                        © {new Date().getFullYear()} Sentinel Systems Group, LLC
                    </p>
                </div>
            </div>
        </footer>
    );
};

// Landing Page Component
function LandingPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showSplash, setShowSplash] = useState(true);
    const [contentReady, setContentReady] = useState(false);
    const [contentEmerging, setContentEmerging] = useState(false);

    const handleEmerge = useCallback(() => {
        setContentReady(true);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setContentEmerging(true);
            });
        });
    }, []);

    const handleSplashComplete = useCallback(() => {
        setShowSplash(false);
    }, []);

    return (
        <div className="min-h-screen bg-black" data-testid="app-container">
            <Toaster position="top-right" richColors theme="dark" />
            
            {showSplash && (
                <SplashScreen 
                    onEmerge={handleEmerge} 
                    onComplete={handleSplashComplete} 
                />
            )}
            
            {contentReady && (
                <div className={`main-content ${contentEmerging ? 'content-visible' : 'content-hidden'}`}>
                    <Navigation 
                        mobileMenuOpen={mobileMenuOpen} 
                        setMobileMenuOpen={setMobileMenuOpen} 
                    />
                    <main>
                        <HeroSection />
                        <MissionSection />
                        <EcosystemSection />
                        <FounderSection />
                        <FoundationSection />
                        <ContactSection />
                    </main>
                    <Footer />
                </div>
            )}
        </div>
    );
}

// Main App Component with Routing
function App() {
    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={
                <Suspense fallback={
                    <div className="flex items-center justify-center h-screen bg-[#050709] text-cyan-400 font-mono text-sm tracking-widest">
                        LOADING...
                    </div>
                }>
                    <AboutUs />
                </Suspense>
            } />
            <Route path="/vault" element={
                <Suspense fallback={
                    <div className="flex items-center justify-center h-screen bg-slate-950 text-cyan-400 font-mono text-sm tracking-widest">
                        INITIALIZING VAULT...
                    </div>
                }>
                    <GuardianVault />
                </Suspense>
            } />
        </Routes>
    );
}

export default App;
